import { Box } from '@modusign/design-system';
import { createInitialLastInsertedField, useSigningState } from '@modusign/react-signing/setup';
import { ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { useMount, useUnmount } from 'react-use';
import { v4 } from 'uuid';

import { toSigningStateForSetup } from '@app/features/apply-react-signing/libs/signing-mapper';
import { getUpload } from '@app/features/contractLink/contractLinkCreate/selectors';
import ContractLinkCreationSource from '@app/features/contractLink/contractLinkCreate/types/ContractLinkCreationSource';
import { createDataLabel } from '@app/features/documents/utils/createDataLabel';
import { usePDF } from '@app/hooks/usePDF';
import PDFDocumentPageLayout from '@app/libs/reactPdfJs/components/PDFDocumentPageLayout';
import workspaceService from '@app/libs/workspaceService';
import { clearDocumentSetupState } from '@app/store/documentSetup/actions';
import { templateClearAllState } from '@app/store/template/actions';
import { getFileFromURL } from '@app/store/transport-layer/files';
import { getTemplateById } from '@app/store/transport-layer/template';
import { copyTemplateRequesterAttachments } from '@app/store/transport-layer/template/templates/attachments/copy';
import { getUser } from '@app/store/user/selectors';

interface ContractLinkCreateLayoutProps {
  children: ReactNode;
}

export interface ContractLinkCreateLayoutLocationState {
  source?: ContractLinkCreationSource;
}

function ContractLinkCreateLayout({ children }: ContractLinkCreateLayoutProps) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<ContractLinkCreateLayoutLocationState>();
  const user = useSelector(getUser);
  const { initialize } = useSigningState();
  const { fileName, fileId, url } = useSelector(getUpload);
  const { setPDF, resetPDF } = usePDF();

  useMount(() => {
    initializeOnMount();

    async function initializeOnMount() {
      if (!url || !fileName || !fileId) {
        history.replace('/links');
        return;
      }

      resetPDF();

      const signingState: Parameters<typeof initialize>[0] = {
        attachments: [],
        document: {
          // (@DevSDK): document를 template data 핸들링하는데 사용할지 고려하기
          // FYI: https://modusign.slack.com/archives/C04902B1JF7/p1674694203898699
          title: fileName,
          pdfFile: {
            url,
            id: fileId,
          },
          pdfPassword: null,
        },
        fields: [],
        participants: [
          {
            type: 'REQUESTER',
            name: user.user.name,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            contact: user.user.email!,
            id: v4(),
            dataLabel: createDataLabel(),
          },
        ],
        verifications: [],
        ui: {
          isInOrder: false, // 링크 서명은 항상 순서 없는 서명
          insertingField: null,
          lastInsertedField: createInitialLastInsertedField(),
        },
      };

      const templateId = location.state.source?.id;
      if (location.state.source?.type === 'template' && templateId) {
        const workspaceId = workspaceService.getWorkspaceId();

        const [attachmentCopyResponse, template] = await Promise.all([
          copyTemplateRequesterAttachments(workspaceId, templateId),
          getTemplateById(workspaceId, templateId),
        ]);

        const { attachments, fields, participants } = toSigningStateForSetup(
          attachmentCopyResponse.attachments,
          template.participants,
          template.fields,
          template.fieldGroups,
          []
        );

        signingState.attachments = attachments;
        signingState.fields = fields;
        signingState.participants = participants;
      }

      initialize(signingState);

      const { data: pdf } = await getFileFromURL(url);
      setPDF(pdf);
    }
  });

  useUnmount(() => {
    initialize();
    resetPDF();
    dispatch(clearDocumentSetupState());
    dispatch(templateClearAllState());
  });

  return (
    <Box
      backgroundColor="bodySecondaryBase"
      height="100%"
      overflow="hidden"
      sx={{
        '&::before': {
          background: 'white',
          backgroundColor: 'bodySecondaryBase',
          content: '""',
          display: 'block',
          left: 0,
          minHeight: '200vh',
          position: 'fixed',
          top: '-50vh',
          width: '100vw',
          zIndex: -1,
        },
      }}
    >
      <PDFDocumentPageLayout>{children}</PDFDocumentPageLayout>
    </Box>
  );
}

export default ContractLinkCreateLayout;
